"use client";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { AlertCircleIcon, Loader2Icon, XIcon } from "lucide-react";
import React, {
  useActionState,
  useCallback,
  useMemo,
  useState,
  useTransition,
} from "react";
import { toast } from "sonner";
import { useServerActionErrorHandler } from "@/app/[lang]/_over-capacity";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { logger } from "@/lib/logger";
import { useRouter } from "@/lib/navigation";
import { useRefreshWorkspaceSidebar } from "../../_contexts/workspace-sidebar-data-context";
import { addPresetsToUserAnalytics } from "../../action";
import { CategoryTree } from "../../ua/_components/category-tree";
import { PresetCards } from "../../ua/_components/preset-cards";
import { usePresetSelection } from "../../ua/_hooks/use-preset-selection";
import { getAllPresetsAsViewPresets } from "../../ua/_utils/preset-config";

interface CreateViewFromPresetDialogProps {
  workspaceId: string;
  projectId: string;
  userAnalyticsId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateViewFromPresetDialog = React.memo(
  function CreateViewFromPresetDialog({
    workspaceId,
    projectId,
    userAnalyticsId,
    open,
    onOpenChange,
  }: CreateViewFromPresetDialogProps) {
    const { i18n } = useLingui();
    const router = useRouter();
    const { handleServerActionError } = useServerActionErrorHandler();
    const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();

    // Form state
    const [errors, setErrors] = useState<string[]>([]);

    // Get presets
    const viewPresets = useMemo(() => {
      return getAllPresetsAsViewPresets(i18n);
    }, [i18n]);

    // Custom hook for preset selection
    const presetSelection = usePresetSelection(viewPresets, i18n, false); // プリセット追加時はデフォルトで何も選択しない

    // Form action with validation
    const handleCreateViewsFromPresets = useCallback(
      async (_prevState: unknown, _formData: FormData) => {
        try {
          setErrors([]);

          // Client-side validation
          if (presetSelection.selectedPresetIds.length === 0) {
            const error = i18n._(msg`Please select at least one preset`);
            setErrors([error]);
            return { errors: [error] };
          }

          // Server action call
          const result = await addPresetsToUserAnalytics(
            userAnalyticsId,
            presetSelection.selectedPresetIds,
          );

          if (result.errors && result.errors.length > 0) {
            setErrors(result.errors);
            logger.warn("Failed to create views from presets:", result.errors);

            // Over Capacityエラーをチェックしてモーダル表示
            handleServerActionError(result);

            return result;
          }

          // Reset selection for next time
          presetSelection.clearSelection();

          // プロジェクトの一覧画面へ遷移
          const projectPath = `/${encodeURIComponent(workspaceId)}/ua/${encodeURIComponent(projectId)}`;
          router.push(projectPath);
          router.refresh();

          // Success
          // 成功メッセージを表示
          const selectedCount = presetSelection.selectedPresetIds.length;
          toast.success(
            i18n._(msg`${selectedCount} views created from presets`),
          );

          // ダイアログを閉じる
          onOpenChange(false);
          refreshWorkspaceSidebar("project-mutation");

          return { errors: [] };
        } catch (error) {
          const errorMessage = i18n._(
            msg`An unexpected error occurred. Please try again.`,
          );
          setErrors([errorMessage]);
          logger.error(
            "Unexpected error during view creation from presets:",
            error,
          );
          return { errors: [errorMessage] };
        }
      },
      [
        userAnalyticsId,
        presetSelection.selectedPresetIds,
        i18n,
        handleServerActionError,
        onOpenChange,
        presetSelection,
        workspaceId,
        projectId,
        router,
        refreshWorkspaceSidebar,
      ],
    );

    const [_state, formAction, isPending] = useActionState(
      handleCreateViewsFromPresets,
      null,
    );

    const [isTransitionPending, startTransition] = useTransition();

    // Submit handler
    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault();
        const form = e.currentTarget as HTMLFormElement;
        const formData = new FormData(form);

        // Call formAction inside startTransition
        startTransition(() => {
          formAction(formData);
        });
      },
      [formAction],
    );

    const pending = isPending || isTransitionPending;

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          key={open ? "open" : "closed"}
          className="max-w-4xl max-h-[90vh] flex flex-col p-0"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              <Trans>Create View from Preset</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                Select preset views to add to your User Analytics project.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-hidden flex flex-col min-h-0"
          >
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
              <div className="space-y-4">
                {/* Error Display */}
                {errors.length > 0 && (
                  <Alert variant="destructive">
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertTitle>
                      <Trans>Error</Trans>
                    </AlertTitle>
                    <AlertDescription>
                      {errors.map((error, index) => (
                        <div key={index}>{error}</div>
                      ))}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Preset Selection */}
                <div className="flex flex-col gap-3">
                  <ResizablePanelGroup
                    direction="horizontal"
                    className="min-h-[400px] h-[400px] rounded-lg border"
                  >
                    {/* Category Tree */}
                    <ResizablePanel defaultSize={35} minSize={25} maxSize={50}>
                      <CategoryTree
                        categories={presetSelection.categories}
                        selectedPresetIds={presetSelection.selectedPresetIds}
                        expandedCategories={presetSelection.expandedCategories}
                        onPresetToggle={presetSelection.handlePresetToggle}
                        onCategoryToggle={presetSelection.handleCategoryToggle}
                        onSelectAll={presetSelection.handleSelectAll}
                        viewPresets={viewPresets}
                      />
                    </ResizablePanel>

                    <ResizableHandle withHandle />

                    {/* Preset Cards - 2 column layout */}
                    <ResizablePanel defaultSize={65}>
                      <PresetCards
                        presets={presetSelection.filteredPresets}
                        selectedPresetIds={presetSelection.selectedPresetIds}
                        searchQuery={presetSelection.searchQuery}
                        onSearchChange={presetSelection.setSearchQuery}
                        onPresetToggle={presetSelection.handlePresetToggle}
                      />
                    </ResizablePanel>
                  </ResizablePanelGroup>

                  {/* Selected presets display (always visible) */}
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg min-h-[120px]">
                    <div className="text-xs font-semibold text-foreground mb-2">
                      <Trans>Selected Presets</Trans>{" "}
                      {presetSelection.selectedPresetIds.length > 0 &&
                        `(${presetSelection.selectedPresetIds.length})`}
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[80px] content-start">
                      {presetSelection.selectedPresetIds.length > 0 ? (
                        presetSelection.selectedPresetIds.map((presetId) => {
                          const preset = viewPresets.find(
                            (p) => p.presetID === presetId,
                          );
                          if (!preset) return null;
                          return (
                            <div
                              key={presetId}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs h-fit"
                            >
                              <span className="truncate max-w-[200px]">
                                {preset.name}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  presetSelection.handlePresetToggle(presetId)
                                }
                                className="ml-1 hover:bg-primary/20 rounded p-0.5"
                              >
                                <XIcon className="h-3 w-3" />
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-xs text-muted-foreground flex items-center">
                          <Trans>No presets selected</Trans>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="px-6 py-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                type="submit"
                disabled={
                  pending || presetSelection.selectedPresetIds.length === 0
                }
                className="w-20"
              >
                {pending ? (
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                ) : (
                  <Trans>Create</Trans>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  },
);
